import { InAppState } from "../base/InAppState";
import template from "./AnalyticsTemplate.html";
import { _lcs, locale } from "../../i18n";
import { lineChart, columns, bars, bind } from "../../core/UsageCharts";

const DEFAULT_RANGES = [ 7, 30, 90, 180, 365 ];
const DEFAULT_DAYS = 30;
// From this range on the line shows weeks (see client-admin StatisticsState).
const WEEKLY_FROM_DAYS = 180;

// Guest-app section → German source string (translated where it is read).
const AREA_LABELS = {
    home            : "Startseite",
    info            : "Gästemappe",
    recommendations : "Empfehlungen",
    region          : "Region",
    events          : "Veranstaltungen",
    plan            : "Aufenthaltsplaner",
    chat            : "Chat",
    feedback        : "Umfragen",
    spa             : "Wellness",
    other           : "Sonstiges"
};

/**
 * AnalyticsState — guest-app usage across every accommodation
 * (GET /admin/analytics): visitors over time, the most used houses, which
 * sections guests open and when.
 *
 * The same anonymous daily rollups the hosts see on their "Statistik" screen,
 * summed over all houses — a visitor is someone on one day at one house, so
 * the totals are visitor-days, not people.
 */
export class AnalyticsState extends InAppState
{
    static ROUTE = "/analytics";
    static ID = "analytics";

    getContentHeaderData()
    {
        return {
            title : _lcs( "Analytics" ),
            subtitle : _lcs( "Nutzung der Gäste-Apps aller Unterkünfte" )
        };
    }

    async enter()
    {
        this.days = DEFAULT_DAYS;

        const root = this.app.view.pageContent;
        root.innerHTML = '<div class="an" data-id="an"></div>';
        this.body = root.querySelector( '[data-id="an"]' );
        this.addEventListener( this.body, "click", ( e ) => this.onClick( e ) );

        this._showMessage( _lcs( "Lade Analytics …" ) );
        await this.loadAndRender();
    }

    onClick( e )
    {
        const period = e.target.closest( "[data-days]" );
        if ( period && +period.getAttribute( "data-days" ) !== this.days )
        {
            this.days = +period.getAttribute( "data-days" );
            this.loadAndRender();
        }
    }

    async loadAndRender()
    {
        this.body.classList.add( "an--loading" );

        let data = null;
        try
        {
            const { result } = await this.app.api.get( "/admin/analytics?days=" + this.days );
            if ( +result?.status === 200 && result.data )
            {
                data = result.data;
            }
        }
        catch ( err )
        {
            console.error( err );
        }

        if ( !this.isActive() )
        {
            return;
        }
        this.body.classList.remove( "an--loading" );

        if ( !data )
        {
            this._showMessage( _lcs( "Analytics konnten nicht geladen werden." ) );
            return;
        }

        const vm = this.viewModel( data );
        this.app.view.render( this.body, template, vm );
        bind( this.body, vm.interactive, ( el, type, fn ) => this.addEventListener( el, type, fn ) );
    }

    viewModel( data )
    {
        const t = data.totals,
              p = data.previous,
              points = this.linePoints( data ),
              weekdayItems = this.weekdayItems( data.weekday ),
              hourItems = this.hourItems( data.details.hour );

        return {
            hasData : t.views > 0 || data.series.some( ( s ) => s.visitors > 0 ),
            periods : ( data.range.ranges || DEFAULT_RANGES ).map( ( d ) => ( {
                value : d, label : _lcs( "{days} Tage", { days : d } ), active : d === this.days
            } ) ),
            rangeLabel : this.dateLabel( data.range.from ) + " – " + this.dateLabel( data.range.to ),
            asOf : data.asOf ? new Intl.DateTimeFormat( locale(), { day : "numeric", month : "long", hour : "2-digit", minute : "2-digit" } ).format( new Date( data.asOf ) ) : null,
            kpis : [
                { label : _lcs( "Besucher" ), icon : "users", value : this.num( t.visitors ), ...this.delta( t.visitors, p.visitors ) },
                { label : _lcs( "Besuche" ), icon : "door-open", value : this.num( t.sessions ), ...this.delta( t.sessions, p.sessions ) },
                { label : _lcs( "Seitenaufrufe" ), icon : "eye", value : this.num( t.views ), ...this.delta( t.views, p.views ) },
                {
                    label : _lcs( "Aktive Unterkünfte" ), icon : "building-2", value : this.num( t.activeAccommodations || 0 ),
                    note : _lcs( "mit Besuchern im Zeitraum" ), noteIcon : "circle-check", noteTone : "flat"
                }
            ],
            lineTitle : data.range.days >= WEEKLY_FROM_DAYS ? _lcs( "Besucher pro Woche" ) : _lcs( "Besucher pro Tag" ),
            lineHtml : lineChart( points, { format : ( v ) => this.num( v ), label : _lcs( "Besucher im Zeitverlauf" ), partialLast : true } ),
            weekdayHtml : columns( weekdayItems, { label : _lcs( "Ø Besucher pro Wochentag" ), showValues : true } ),
            hourHtml : columns( hourItems, { label : _lcs( "Besucher nach Uhrzeit" ), showValues : false } ),
            areasHtml : bars( data.details.area.slice().sort( ( a, b ) => b.count - a.count ).map( ( a ) => ( {
                label : _lcs( AREA_LABELS[ a.key ] || "Sonstiges" ),
                value : a.count,
                display : _lcs( "{n} Aufrufe", { n : this.num( a.count ) } )
            } ) ) ),
            hasAreas : data.details.area.length > 0,
            top : ( data.top || [] ).map( ( row, i ) => ( {
                rank : i + 1,
                name : row.name || _lcs( "Gelöschte Unterkunft" ),
                slug : row.slug || "",
                visitors : this.num( row.visitors ),
                sessions : this.num( row.sessions ),
                views : this.num( row.views )
            } ) ),
            interactive : { line : points, cols : [ weekdayItems, hourItems ] }
        };
    }

    delta( current, previous )
    {
        if ( !( previous > 0 ) )
        {
            return { note : current > 0 ? _lcs( "Kein Vergleich zum Vorzeitraum" ) : _lcs( "Noch keine Daten" ), noteIcon : "minus", noteTone : "flat" };
        }
        const change = Math.round( ( current - previous ) / previous * 100 );
        if ( change === 0 )
        {
            return { note : _lcs( "Wie im Vorzeitraum" ), noteIcon : "minus", noteTone : "flat" };
        }
        return {
            note : _lcs( "{change} zum Vorzeitraum", { change : ( change > 0 ? "+" : "−" ) + this.num( Math.abs( change ) ) + " %" } ),
            noteIcon : change > 0 ? "trending-up" : "chevron-down",
            noteTone : change > 0 ? "up" : "down"
        };
    }

    /** The range ends today, so its last point (day or week) is still being counted. */
    linePoints( data )
    {
        const points = this.periodPoints( data );
        if ( points.length )
        {
            const last = points[ points.length - 1 ];
            last.title += " · " + _lcs( "bis jetzt" );
        }
        return points;
    }

    periodPoints( data )
    {
        const rows = ( s ) => [
            { label : _lcs( "Besucher" ), value : this.num( s.visitors ) },
            { label : _lcs( "Besuche" ), value : this.num( s.sessions ) },
            { label : _lcs( "Seitenaufrufe" ), value : this.num( s.views ) }
        ];

        if ( data.range.days < WEEKLY_FROM_DAYS )
        {
            return data.series.map( ( s ) => ( {
                label : this.dateLabel( s.day ), title : this.dateLabel( s.day, true ), value : s.visitors, rows : rows( s )
            } ) );
        }

        const weeks = [];
        // Whole weeks from the first Monday on: a leading two-day stub would
        // read as a slump. The last week is still running (drawn dashed).
        const firstMonday = data.series.findIndex( ( s ) => ( new Date( s.day + "T00:00:00Z" ).getUTCDay() || 7 ) === 1 );
        data.series.slice( Math.max( 0, firstMonday ) ).forEach( ( s ) =>
        {
            const dow = new Date( s.day + "T00:00:00Z" ).getUTCDay() || 7;
            if ( !weeks.length || dow === 1 )
            {
                weeks.push( { start : s.day, visitors : 0, sessions : 0, views : 0 } );
            }
            const w = weeks[ weeks.length - 1 ];
            w.visitors += s.visitors;
            w.sessions += s.sessions;
            w.views += s.views;
        } );
        return weeks.map( ( w ) => ( {
            label : this.dateLabel( w.start ),
            title : _lcs( "Woche ab {date}", { date : this.dateLabel( w.start, true ) } ),
            value : w.visitors,
            rows : rows( w )
        } ) );
    }

    weekdayItems( weekday )
    {
        const short = new Intl.DateTimeFormat( locale(), { weekday : "short", timeZone : "UTC" } ),
              long = new Intl.DateTimeFormat( locale(), { weekday : "long", timeZone : "UTC" } );
        return weekday.map( ( w ) =>
        {
            // 2024-01-01 was a Monday.
            const date = new Date( Date.UTC( 2024, 0, w.isoDow ) );
            return {
                label : short.format( date ),
                title : long.format( date ),
                value : w.avgVisitors,
                display : this.num( w.avgVisitors, w.avgVisitors < 10 ? 1 : 0 )
            };
        } );
    }

    hourItems( hours )
    {
        return hours.map( ( h ) =>
        {
            const hour = +h.key;
            return {
                label : hour % 3 === 0 ? String( hour ) : "",
                title : _lcs( "{from}–{to} Uhr", { from : hour, to : ( hour + 1 ) % 24 } ),
                value : h.visitors,
                display : this.num( h.visitors )
            };
        } );
    }

    num( value, digits = 0 )
    {
        return new Intl.NumberFormat( locale(), { maximumFractionDigits : digits } ).format( value || 0 );
    }

    dateLabel( iso, long = false )
    {
        return new Intl.DateTimeFormat( locale(), long
            ? { day : "numeric", month : "long", year : "numeric", timeZone : "UTC" }
            : { day : "numeric", month : "short", timeZone : "UTC" } ).format( new Date( iso + "T12:00:00Z" ) );
    }

    _showMessage( text )
    {
        this.body.innerHTML = '<p class="an-muted"></p>';
        this.body.querySelector( "p" ).textContent = text;
    }
}
